import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, User, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Footer } from '../components/Footer';
export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();

  // Load remembered username on mount
  useEffect(() => {
    const rememberedUsername = localStorage.getItem('criket_remembered_username');
    if (rememberedUsername) {
      setUsername(rememberedUsername);
      setRememberMe(true);
    }
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        if (rememberMe) {
          localStorage.setItem('criket_remembered_username', username);
        } else {
          localStorage.removeItem('criket_remembered_username');
        }
        navigate('/admin/orders');
      } else {
        setError('Usuario o contraseña incorrectos');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Error al iniciar sesión. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    // min-h-[100dvh] asegura que ocupe el alto real en móviles ignorando la barra de navegación
    <div className="min-h-[100dvh] bg-gradient-to-br from-gray-50 via-white to-gray-50 flex flex-col font-sans" data-id="element-1855">
      
      {/* Contenedor Principal Centrado con Scroll seguro */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 w-full" data-id="element-1856">
        
        <div className="w-full max-w-[350px] sm:max-w-md mx-auto flex flex-col justify-center" data-id="element-1857">
          
          {/* Logo Section - Adaptable */}
          <div className="text-center mb-6 sm:mb-8" data-id="element-1858">
            <div className="inline-flex items-center justify-center" data-id="element-1859">
              <img src="https://i.imgur.com/bxPZvDb.png" alt="Cricket Logo"
              // Altura responsiva: h-16 en móvil, h-24 en tablet/desktop
              className="h-16 sm:h-24 w-auto object-contain transition-all duration-300" data-id="element-1860" />
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden w-full" data-id="element-1861">
            <div className="p-6 sm:p-8" data-id="element-1862">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center sm:text-left" data-id="element-1863">
                Iniciar Sesión
              </h2>

              {error && <div className="mb-6 p-3 sm:p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3 animate-pulse" data-id="element-1864">
                  <AlertCircle className="text-red-600 shrink-0 mt-0.5" size={18} data-id="element-1865" />
                  <div data-id="element-1866">
                    <p className="text-sm font-medium text-red-900" data-id="element-1867">{error}</p>
                    <p className="text-xs text-red-700 mt-1" data-id="element-1868">
                      Verifica tus credenciales
                    </p>
                  </div>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1869">
                {/* Usuario */}
                <div data-id="element-1870">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1871">
                    Usuario
                  </label>
                  <div className="relative group" data-id="element-1872">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1873">
                      <User className="h-5 w-5 text-gray-400 group-focus-within:text-black transition-colors" data-id="element-1874" />
                    </div>
                    <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                    // text-base evita zoom en iOS. h-11 o h-12 para mejor touch target
                    className="block w-full pl-10 pr-3 h-11 sm:h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900 text-base shadow-sm transition-all" placeholder="Ingresa tu usuario" required autoFocus data-id="element-1875" />
                  </div>
                </div>

                {/* Contraseña */}
                <div data-id="element-1876">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1877">
                    Contraseña
                  </label>
                  <div className="relative group" data-id="element-1878">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1879">
                      <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-black transition-colors" data-id="element-1880" />
                    </div>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-3 h-11 sm:h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-gray-900 text-base shadow-sm transition-all" placeholder="Ingresa tu contraseña" required data-id="element-1881" />
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center justify-between pt-1" data-id="element-1882">
                  <div className="flex items-center" data-id="element-1883">
                    <input id="remember-me" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer" data-id="element-1884" />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-600 cursor-pointer select-none" data-id="element-1885">
                      Recuérdame
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <Button type="submit" fullWidth size="lg" disabled={isLoading} className="h-11 sm:h-12 text-base font-semibold shadow-md hover:shadow-lg transition-all active:scale-[0.98]" data-id="element-1886">
                  {isLoading ? <span className="flex items-center gap-2" data-id="element-1887">
                      <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" data-id="element-1888">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-1889"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1890"></path>
                      </svg>
                      Procesando...
                    </span> : 'Iniciar Sesión'}
                </Button>
              </form>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center" data-id="element-1891">
            <p className="text-sm text-gray-500" data-id="element-1892">
              ¿Problemas para acceder?{' '}
              <a href="mailto:soporte@lacochera.com" className="text-black font-semibold hover:underline transition-colors p-2" data-id="element-1893">
                Contacta soporte
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer adaptable */}
      <div className="p-4 bg-white/50 backdrop-blur-sm sm:bg-transparent" data-id="element-1894">
        <Footer data-id="element-1895" />
      </div>
    </div>
  );
}